<template>
  <div
    class="slider-small"
    :data-ab-id="dataAbId"
  >
    <div class="slider-small__header">
      <h2
        v-if="topTitle"
        class="slider-small__header--title flex items-end gap-1"
      >
        {{ topTitle }}
        <KikLegalInformationDrawer
          v-if="enableLegalDisclaimer"
          :legal-text="legalText"
          :title="legalTitle"
        />
      </h2>
      <SharedLink
        v-if="link"
        :link="link"
        class="slider-small__header--description"
      >
        {{ ctaText }} <SfIconArrowForward />
      </SharedLink>
    </div>
    <KikCarousel>
      <div
        v-for="(product, index) in products"
        :key="index"
        class="w-[70vw] shrink-0 rounded-md first:ms-auto last:me-auto hover:shadow-lg md:w-[400px] lg:w-[500px] xl:w-[600px]"
      >
        <SharedTeaserCard
          :title="product.title"
          :title-color="product.titleColor"
          :introduction="product.introduction"
          :introduction-color="product.introductionColor"
          :src="product.src"
          :src-set="product.srcSet"
          :alt="product.alt"
          :link="product.ctaLink"
        />
      </div>
    </KikCarousel>
  </div>
</template>

<script lang="ts" setup>
import { SfIconArrowForward } from '@storefront-ui/vue';
import magnoliaGetters from '~/composables/getters/magnoliaGetters';

interface Props {
  topTitle?: string;
  topTitleColor?: string;
  ctaTextColor?: string;
  ctaLink?: any[];
  slides?: any[];
  dataAbId?: string;
  legalText?: string;
  legalTitle?: string;
  enableLegalDisclaimer?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  topTitle: '',
  topTitleColor: '',
  ctaTextColor: '',
  dataAbId: '',
  legalText: '',
  legalTitle: '',
  enableLegalDisclaimer: false,
  ctaLink: () => ([]),
  slides: () => ([]),
});

const { ctaLink, slides } = toRefs(props);
const { $domain } = useNuxtApp();

const products = computed(() => (
  slides.value?.map(slide => ({
    ...slide,
    introduction: slide.subTitle,
    introductionColor: slide.subTitleColor,
    src: magnoliaGetters.getDefaultSrcAsset(slide.image, $domain.cmsAssetDomainPath),
    srcSet: magnoliaGetters.getSrcSetImagesAsString(slide.image, $domain.cmsAssetDomainPath, slide.aspectRatio, slide.mobileAspectRatio?.[0]?.aspectRatio),
    alt: slide.image?.metadata?.caption ?? '',
    ctaLink: slide.ctaLink?.[0] ? magnoliaGetters.getLinkAndName(slide.ctaLink[0], $domain.cmsAssetDomainPath, slide.ctaLink[0].switchable?.intLinkParam) : {},
  }))
));

const link = computed(() => (ctaLink.value?.[0] ? magnoliaGetters.getLinkAndName(ctaLink.value[0], $domain.cmsAssetDomainPath, ctaLink.value[0].switchable?.intLinkParam) : {}));
const ctaText = computed(() => (ctaLink.value?.[0] ? ctaLink.value?.[0].ctaText : ''));
</script>

<style lang="scss" scoped>
.slider-small {
  &__header {
    padding: 20px 20px 0 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;

    @media screen and (max-width: $tablet-max) {
      flex-direction: column;
      align-items: flex-start;
      padding-left: 10px;
    }

    @media screen and (max-width: $mobile-max) {
      padding-left: 20px;
    }

    &--title {
      color: v-bind(topTitleColor);
      font-size: var(--h2-font-size);
      font-weight: var(--font-weight--bold);
    }

    &--description :deep(a) {
      display: flex;
      align-items: center;
      text-transform: uppercase;
      gap: 20px;
      font-weight: var(--font-weight--bold);
      font-size: var(--font-size--base);
      color: v-bind(ctaTextColor);

      margin: 0;
    }
  }
}
</style>
